import React from "react";
import { cx } from "./cx";
import {
  IconChevronDown,
  IconCalendar,
  IconCircleCheck,
  IconUser,
  IconNavigate,
  IconCar,
  IconCheck,
  IconX,
  IconCircle,
  IconClock,
} from "./icons";

// Re-export the newer primitives (split into their own files) so the whole
// design system is reachable from a single import path: `from '../ui/primitives'`.
export { Table } from "./Table";
export { Modal, Drawer, ConfirmDialog } from "./Modal";
export { Tabs } from "./Tabs";
export { DropdownMenu } from "./DropdownMenu";
export { Alert } from "./Alert";
export { ToastProvider, useToast } from "./Toast";
export { Pagination } from "./Pagination";

// All primitives are built from the Movorra brand tokens defined in
// tailwind.config.js (primary navy, accent amber, surface, border, body,
// success/danger/info) — never raw indigo/slate/blue palette utilities or hex.
// Accent / tone / variant KEYS are kept stable so existing page calls
// (accent="indigo", variant="outlineIndigo", …) keep resolving.
//
// REFINEMENT NOTES (this pass):
// - Unified focus to focus-visible (keyboard-only ring; no ring on mouse click)
//   across every interactive control via FOCUS_RING.
// - Every button/pill/page-button carries a 1px border (transparent on filled
//   variants) so filled and outlined controls are the SAME height and line up
//   in a toolbar. Previously filled = 36px, outlined = 38px.
// - All count chips are font-mono (counts are data, per brand).
// - Navy-tinted hover everywhere (hover:bg-primary/5) instead of the
//   near-invisible bg-surface.

const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

const CARD_ACCENT_LEFT = {
  indigo: "border-l-4 border-l-primary",
  amber: "border-l-4 border-l-accent",
  emerald: "border-l-4 border-l-success",
  violet: "border-l-4 border-l-info",
  rose: "border-l-4 border-l-danger",
  slate: "border-l-4 border-l-border",
};

export function Card({
  className = "",
  interactive = false,
  accent,
  gradientTop = false,
  children,
  ...rest
}) {
  return (
    <div
      className={cx(
        "bg-white rounded-card border border-border shadow-card",
        accent && CARD_ACCENT_LEFT[accent],
        gradientTop && "accent-gradient-top",
        interactive &&
          "transition-all duration-150 cursor-pointer hover:shadow-pop hover:border-primary/30 " +
            "hover:-translate-y-0.5 active:translate-y-0 active:shadow-card",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Field({ label, children, hint, tone = "light" }) {
  const peoria = tone === "peoria";
  return (
    <label className="block space-y-1.5">
      <div className={cx("text-sm font-medium", peoria ? "text-peoria-text" : "text-body")}>{label}</div>
      {children}
      {/* space-y handles the gap — removed the redundant mt-1 that double-spaced the hint */}
      {hint && <div className={cx("text-xs", peoria ? "text-peoria-muted" : "text-body/60")}>{hint}</div>}
    </label>
  );
}

const INPUT_BASE =
  "w-full rounded-btn border border-border bg-white text-sm text-body placeholder:text-body/50 " +
  "px-3 py-2 transition-colors duration-150 enabled:hover:border-body/30 " +
  "focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 " +
  "disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-surface";

// Opt-in dark input chrome (charter forms only). R3 keeps INPUT_BASE unchanged.
const INPUT_BASE_PEORIA =
  "w-full rounded-btn border border-peoria-border bg-peoria-surface2 text-sm text-peoria-text placeholder:text-peoria-muted " +
  "px-3 py-2 transition-colors duration-150 enabled:hover:border-peoria-muted/40 " +
  "focus:outline-none focus:border-peoria-red focus:ring-2 focus:ring-peoria-red/30 " +
  "disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-peoria-surface";

export function TextInput({
  value,
  onChange,
  placeholder,
  icon,
  type = "text",
  className = "",
  tone = "light",
  ...rest
}) {
  const base = tone === "peoria" ? INPUT_BASE_PEORIA : INPUT_BASE;
  const iconTone = tone === "peoria" ? "text-peoria-muted" : "text-body/50";
  return (
    <div className={cx("relative", className)}>
      {icon && (
        <div className={cx("absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none", iconTone)}>
          {icon}
        </div>
      )}
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        {...rest}
        className={cx(base, icon && "pl-9")}
      />
    </div>
  );
}

export function Select({ value, onChange, icon, children, className = "", tone = "light", ...rest }) {
  const base = tone === "peoria" ? INPUT_BASE_PEORIA : INPUT_BASE;
  const iconTone = tone === "peoria" ? "text-peoria-muted" : "text-body/50";
  return (
    <div className={cx("relative", className)}>
      {icon && (
        <div className={cx("absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none", iconTone)}>
          {icon}
        </div>
      )}
      <select
        value={value}
        onChange={onChange}
        {...rest}
        className={cx(base, "appearance-none cursor-pointer pr-9", icon && "pl-9")}
      >
        {children}
      </select>
      <div className={cx("absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none", iconTone)}>
        <IconChevronDown size={14} />
      </div>
    </div>
  );
}

export function Button({
  variant = "primary",
  size = "md",
  className = "",
  children,
  ...rest
}) {
  // Each variant supplies exactly ONE border-color utility (transparent on
  // filled variants) so the base `border` width never collides and all
  // variants share one height. Ring COLOR is per-variant; ring width/offset
  // come from the base.
  const variants = {
    primary:
      "border-transparent bg-primary text-white shadow-card hover:bg-primary-dark " +
      "focus-visible:ring-primary",
    hero:
      // navy-tinted shadows now (was gray Tailwind shadow-md/shadow-lg)
      "border-transparent bg-primary text-white shadow-card hover:bg-primary-dark hover:shadow-pop " +
      "focus-visible:ring-primary",
    outline:
      "border-border bg-white text-body hover:bg-surface " +
      "focus-visible:ring-primary",
    // Kept for back-compat (was "outlineIndigo"); a navy outline.
    outlineIndigo:
      "border-primary/40 bg-white text-primary hover:bg-primary/5 hover:border-primary/60 " +
      "focus-visible:ring-primary",
    ghost:
      "border-transparent text-body hover:text-primary hover:bg-border/40 " +
      "focus-visible:ring-primary",
    // Kept for back-compat (was "emerald"); the success token.
    emerald:
      "border-transparent bg-success text-white shadow-card hover:bg-success/90 " +
      "focus-visible:ring-success",
    danger:
      "border-transparent bg-danger text-white shadow-card hover:bg-danger/90 " +
      "focus-visible:ring-danger",
    // Peoria charter dark theme — brand-red filled button (opt-in).
    peoria:
      "border-transparent bg-peoria-red text-white shadow-card hover:bg-peoria-red-dark " +
      "focus-visible:ring-peoria-red",
    // Peoria charter dark theme — outline/secondary on dark surfaces.
    peoriaOutline:
      "border-peoria-border bg-peoria-surface2 text-peoria-text hover:border-peoria-red/60 hover:bg-peoria-red/10 " +
      "focus-visible:ring-peoria-red",
  };
  const sizes = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-2.5 text-sm",
    xl: "px-6 py-3 text-sm",
  };
  return (
    <button
      {...rest}
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-btn border font-medium cursor-pointer select-none",
        "transition-[background-color,border-color,box-shadow,transform] duration-150",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        "active:translate-y-px",
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:active:translate-y-0",
        variants[variant],
        sizes[size],
        className
      )}
    >
      {children}
    </button>
  );
}

export function Pill({ active, onClick, children, count, className = "" }) {
  return (
    <button
      onClick={onClick}
      className={cx(
        "inline-flex items-center gap-2 rounded-btn border px-4 py-2 text-sm font-medium cursor-pointer select-none",
        "transition-[background-color,border-color,box-shadow,transform] duration-150 active:translate-y-px",
        FOCUS_RING,
        active
          ? "border-transparent bg-primary text-white shadow-card hover:bg-primary-dark"
          : "border-border bg-white text-body hover:bg-surface hover:text-primary",
        className
      )}
    >
      {children}
      {count !== undefined && (
        <span
          className={cx(
            "rounded-md px-1.5 py-0.5 text-11 font-mono font-semibold tabular-nums",
            active ? "bg-white/20 text-white" : "bg-border/60 text-body"
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// Status badge tone classes — all use the same base shape.
const BADGE_BASE =
  "inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-0.5 rounded-full border";
const BADGE_TONES = {
  indigo: "bg-primary/10 text-primary border-primary/20",
  amber: "bg-accent/15 text-primary border-accent/40",
  emerald: "bg-success/10 text-success border-success/30",
  rose: "bg-danger/10 text-danger border-danger/30",
  slate: "bg-surface text-body border-border",
};

const STATUS_MAP = {
  // indigo — confirmed/assigned/upcoming
  upcoming: { label: "Upcoming", tone: "indigo" },
  assigned: { label: "Assigned", tone: "indigo" },
  confirmed: { label: "Confirmed", tone: "indigo" },
  scheduled: { label: "Scheduled", tone: "indigo" },
  accepted: { label: "Accepted", tone: "indigo" },
  sent: { label: "Sent", tone: "amber" },
  // amber — in-progress/pending/sent
  pending: { label: "Pending", tone: "amber" },
  in_progress: { label: "In Progress", tone: "amber" },
  in_transit: { label: "In Transit", tone: "amber" },
  en_route: { label: "En Route", tone: "amber" },
  picked_up: { label: "Picked Up", tone: "amber" },
  // emerald — completed/paid/active
  completed: { label: "Completed", tone: "emerald" },
  dropped_off: { label: "Dropped Off", tone: "emerald" },
  paid: { label: "Paid", tone: "emerald" },
  approved: { label: "Approved", tone: "emerald" },
  converted: { label: "Converted", tone: "emerald" },
  active: { label: "Active", tone: "emerald" },
  // rose — cancelled/failed/inactive
  cancelled: { label: "Cancelled", tone: "rose" },
  canceled: { label: "Cancelled", tone: "rose" },
  failed: { label: "Failed", tone: "rose" },
  expired: { label: "Expired", tone: "rose" },
  declined: { label: "Declined", tone: "rose" },
  // slate — draft/neutral/inactive
  draft: { label: "Draft", tone: "slate" },
  unassigned: { label: "Unassigned", tone: "slate" },
  inactive: { label: "Inactive", tone: "slate" },
};

// ADDITIVE (Dispatch redesign): a per-status Lucide glyph so same-tone statuses
// stay individually legible. Keyed by the same status keys as STATUS_MAP above.
// Opt-in via `glyph` — default OFF so every existing call site is byte-for-byte
// unchanged; only callers that pass `glyph` (e.g. the Dispatch board) get marks.
const STATUS_GLYPHS = {
  scheduled: IconCalendar,
  upcoming: IconCalendar,
  confirmed: IconCircleCheck,
  accepted: IconCircleCheck,
  assigned: IconUser,
  en_route: IconNavigate,
  in_progress: IconNavigate,
  in_transit: IconNavigate,
  sent: IconNavigate,
  pending: IconClock,
  picked_up: IconCar,
  completed: IconCheck,
  dropped_off: IconCheck,
  paid: IconCheck,
  approved: IconCheck,
  converted: IconCheck,
  active: IconCheck,
  cancelled: IconX,
  canceled: IconX,
  failed: IconX,
  expired: IconX,
  declined: IconX,
  draft: IconCircle,
  unassigned: IconCircle,
  inactive: IconCircle,
};

export function StatusBadge({ status, className = "", glyph = false }) {
  const key = (status || "").toString().toLowerCase();
  const m = STATUS_MAP[key] || { label: status || "—", tone: "slate" };
  const Glyph = glyph ? STATUS_GLYPHS[key] || IconCircle : null;
  return (
    <span className={cx(BADGE_BASE, BADGE_TONES[m.tone], className)}>
      {Glyph && <Glyph size={12} stroke={2.25} />}
      {m.label}
    </span>
  );
}

export function PageHeader({ title, subtitle, right }) {
  return (
    <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
      <div>
        <h1 className="text-2xl font-bold text-primary tracking-tight text-balance">{title}</h1>
        {subtitle && <p className="text-sm text-body/70 mt-1">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function DemoBadge({ className = "", title = "This element uses hardcoded data, not a live API." }) {
  return (
    <span
      title={title}
      className={cx(
        "inline-flex items-center text-xs px-2.5 py-0.5 rounded-full",
        "bg-accent/15 text-primary border border-accent/40 font-medium",
        className
      )}
    >
      Demo
    </span>
  );
}
